import { ArrowLeft, Loader2, Save, Trash2, Users, Building2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import type { KontaktDetail } from '@/utils/api'

interface KontaktHeaderProps {
  kontakt: KontaktDetail
  hasChanges: boolean
  isSaving: boolean
  isDeleting: boolean
  onSave: () => void
  onDelete: () => void
  onBack: () => void
}

export function KontaktHeader({
  kontakt,
  hasChanges,
  isSaving,
  isDeleting,
  onSave,
  onDelete,
  onBack,
}: KontaktHeaderProps) {
  return (
    <div className="mb-6">
      {/* Back button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-slate-500 hover:text-slate-700 transition-colors mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        Zurück zu Kontakte
      </button>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          {/* Type icon */}
          <div
            className={`p-3 rounded-xl ${
              kontakt.kontakt_type === 'person'
                ? 'bg-blue-100'
                : 'bg-purple-100'
            }`}
          >
            {kontakt.kontakt_type === 'person' ? (
              <Users className="h-8 w-8 text-blue-600" />
            ) : (
              <Building2 className="h-8 w-8 text-purple-600" />
            )}
          </div>

          <div>
            {/* Name */}
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">
              {kontakt.display_name}
            </h1>

            {/* Subtitle with IATA code and formal name */}
            <div className="flex items-center gap-2 mt-1">
              {kontakt.iata_code && (
                <span className="px-2 py-0.5 text-sm font-mono font-medium rounded bg-slate-200 text-slate-700">
                  {kontakt.iata_code}
                </span>
              )}
              {kontakt.kontakt_type === 'company' && kontakt.legal_form && (
                <span className="text-slate-500">
                  {kontakt.legal_form}
                </span>
              )}
              <span className="text-slate-400">
                {kontakt.kontakt_type === 'person' ? 'Person' : 'Unternehmen'}
              </span>
            </div>

            {/* Tags */}
            {kontakt.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {kontakt.tags.map((tag) => (
                  <span
                    key={tag.id}
                    className="px-2 py-0.5 text-xs font-medium rounded-full bg-slate-100 text-slate-700"
                  >
                    {tag.display_name}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3">
          {/* Unsaved indicator */}
          {hasChanges && (
            <span className="text-sm text-amber-600 font-medium">
              Ungespeicherte Änderungen
            </span>
          )}

          {/* Save button */}
          <Button
            onClick={onSave}
            disabled={!hasChanges || isSaving}
            className="gap-2"
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Speichern
          </Button>

          {/* Delete button with confirmation */}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" disabled={isDeleting} className="gap-2">
                {isDeleting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                Löschen
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Kontakt löschen?</AlertDialogTitle>
                <AlertDialogDescription>
                  Möchten Sie den Kontakt "{kontakt.display_name}" wirklich löschen?
                  Diese Aktion kann nicht rückgängig gemacht werden.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                <AlertDialogAction
                  onClick={onDelete}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Löschen
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  )
}
